import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { fmtDateTime } from "@/utils/format";
import { FileText, ArrowRightCircle, Package, Warehouse as WhIcon, Factory, CheckCircle2, Truck, XCircle, RotateCcw, ClipboardCheck } from "lucide-react";

const KIND_ICON = {
  quote_created: FileText,
  quote_converted: ArrowRightCircle,
  order_created: Package,
  order_updated: RotateCcw,
  warehouse_ready: WhIcon,
  production_output: Factory,
  waybill: Truck,
  order_completed: ClipboardCheck,
  order_shipped: CheckCircle2,
  order_cancelled: XCircle,
};

const KIND_COLOR = {
  quote_created: "#0055FF",
  quote_converted: "#0055FF",
  order_created: "#09090B",
  order_updated: "#525252",
  warehouse_ready: "#0055FF",
  production_output: "#FF3B30",
  waybill: "#00A650",
  order_completed: "#00A650",
  order_shipped: "#00A650",
  order_cancelled: "#FF3B30",
};

export default function OrderTimelineDialog({ orderId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !orderId) { setData(null); return; }
    setLoading(true);
    (async () => {
      try {
        const res = await api.get(`/orders/${orderId}/timeline`);
        setData(res.data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Zaman çizelgesi yüklenemedi");
      } finally { setLoading(false); }
    })();
  }, [open, orderId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none border-2 border-[#09090B] bg-white sm:max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="order-timeline-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">
            Zaman Çizelgesi {data?.order_number ? `· ${data.order_number}` : ""}
          </DialogTitle>
          {data?.customer_name && (
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">
              {data.customer_name}
            </div>
          )}
        </DialogHeader>

        {loading || !data ? (
          <div className="p-8 text-center text-sm font-mono text-neutral-500">Yükleniyor…</div>
        ) : data.events.length === 0 ? (
          <div className="p-8 text-center text-sm font-mono text-neutral-500">Kayıtlı olay yok</div>
        ) : (
          <ol className="relative border-l-2 border-[#09090B] ml-4 mt-2 space-y-4">
            {data.events.map((ev, i) => {
              const Icon = KIND_ICON[ev.kind] || Package;
              const color = KIND_COLOR[ev.kind] || "#09090B";
              return (
                <li key={i} className="ml-6" data-testid={`timeline-event-${ev.kind}-${i}`}>
                  <span
                    className="absolute -left-3.5 flex items-center justify-center w-7 h-7 border-2 bg-white"
                    style={{ borderColor: color }}
                  >
                    <Icon size={14} style={{ color }} />
                  </span>
                  <div className="border-2 border-[#09090B] bg-white p-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                      {fmtDateTime(ev.at)}
                    </div>
                    <div className="text-sm font-semibold mt-0.5" style={{ color }}>
                      {ev.label}
                    </div>
                    {ev.by && (
                      <div className="text-xs text-neutral-500 font-mono mt-1">
                        Kullanıcı: {ev.by}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => onOpenChange(false)}>
            Kapat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
